import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext.js';
import { DepartmentProvider } from './contexts/DepartmentContext';
import { StaffProvider } from './contexts/StaffContext';
import { ScheduleProvider } from './contexts/schedule';
import { VacationProvider } from './contexts/VacationContext';
import { SickLeaveProvider } from './contexts/SickLeaveContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import CompanyLoginPage from './pages/auth/CompanyLoginPage';
import CompanyRegisterPage from './pages/auth/CompanyRegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import CompanyDashboard from './pages/admin/CompanyDashboard';
import DepartmentListPage from './pages/admin/DepartmentListPage';
import DepartmentDetailPage from './pages/admin/DepartmentDetailPage';
import StaffManagementPage from './pages/admin/StaffManagementPage';
import StaffDetailPage from './pages/admin/StaffDetailPage';
import StaffFormPage from './pages/admin/StaffFormPage';
import SchedulePlannerPage from './pages/admin/SchedulePlannerPage';
import TeamVacationPage from './pages/admin/TeamVacationPage';
import SickLeavePage from './pages/admin/SickLeavePage';
import ReportsPage from './pages/admin/ReportsPage';
import ReportSelectionPage from './pages/admin/ReportSelectionPage';
import CompanySettingsPage from './pages/admin/CompanySettingsPage';
import PrivacyPage from './pages/PrivacyPage';
import SupportPage from './pages/SupportPage';

// Import styles
import './styles/auth.css';
import './styles/dashboard.css';
import './styles/department.css';
import './styles/staff-core.css';
import './styles/staff-scheduling.css';
import './styles/staff-forms-modals.css';
import './styles/reports.css';
import './styles/holidays.css';
import './styles/vacation.css';
import './styles/sick-leave.css';
import './styles/schedule-planner-layout.css';
import './styles/schedule-planner-components.css';
import './styles/schedule-planner-interactions.css';
import './components/layout/AdminLayout.css';
import './components/layout/Sidebar.css';

// Redirects legacy /admin/reports/:departmentId links to the
// new category-aware route /admin/reports/time-tracking/:departmentId
function LegacyReportRedirect() {
  const { departmentId } = useParams();
  return <Navigate to={`/admin/reports/time-tracking/${departmentId}`} replace />;
}

function App() {
  const { t } = useTranslation('common');

  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<CompanyLoginPage />} />
            <Route path="/register" element={<CompanyRegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* Admin Routes with Sidebar */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <DepartmentProvider>
                    <StaffProvider>
                      <ScheduleProvider>
                        <VacationProvider>
                          <SickLeaveProvider>
                            <AdminLayout />
                          </SickLeaveProvider>
                        </VacationProvider>
                      </ScheduleProvider>
                    </StaffProvider>
                  </DepartmentProvider>
                </ProtectedRoute>
              }
            >
              {/* Dashboard Route */}
              <Route index element={<CompanyDashboard />} />
              <Route path="dashboard" element={<CompanyDashboard />} />

              {/* Department Routes */}
              <Route path="departments" element={<DepartmentListPage />} />
              <Route path="departments/:departmentId" element={<DepartmentDetailPage />} />

              {/* Staff Routes */}
              <Route path="staff" element={<StaffManagementPage />} />
              <Route path="staff/new" element={<StaffFormPage />} />
              <Route path="staff/:staffId" element={<StaffDetailPage />} />
              <Route path="staff/:staffId/edit" element={<StaffFormPage isEdit={true} />} />

              {/* Schedule Planner Route */}
              <Route path="schedule-planner" element={<SchedulePlannerPage />} />

              {/* Team Vacation Route */}
              <Route path="team-vacation" element={<TeamVacationPage />} />

              {/* Sick Leave Route */}
              <Route path="sick-leave" element={<SickLeavePage />} />

              {/* Reports Routes */}
              {/* Selection page - unchanged */}
              <Route path="reports" element={<ReportSelectionPage />} />
              {/* Legacy route: redirects old bookmarks and links to the new category-aware path */}
              <Route path="reports/:departmentId" element={<LegacyReportRedirect />} />
              {/* New category-aware route used by all report pages */}
              <Route path="reports/:category/:departmentId" element={<ReportsPage />} />

              {/* Company Settings Route */}
              <Route path="settings" element={<CompanySettingsPage />} />
            </Route>

            {/* Redirect to login for root path */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;